/**
 * The homepage's words — as few as the page can carry.
 *
 * The page is a picture of the product with a handful of words on it (owner,
 * 2026-09-26: "as concise and compelling as possible … we are relying too
 * heavily on text"). A heading stands alone unless one short line earns its
 * place; the visual beside it says the rest. The physics is a reason to trust
 * the result, never the pitch.
 *
 * Copy minimalism (site-wide rule): every string either names a step, says
 * what the visitor gets, or states a fact they need. Count the words before
 * adding one.
 */

export const HOME_HERO = {
  headline: 'Design your entire audio system.',
  sub: 'Choose your gear. Build your room. Optimize the acoustics.',
  cta: 'Start building',
  /** Signed in: back into the last project, by name. */
  continue: 'Continue',
} as const;

export interface JourneyStep {
  title: string;
  line: string;
}

/** The five steps, in the order a system is designed. */
export const HOME_JOURNEY: readonly JourneyStep[] = [
  { title: 'Choose your gear', line: 'Real products that fit together.' },
  { title: 'Build your room', line: 'Draw it in minutes.' },
  { title: 'Place your system', line: 'Speakers and seat, to the centimeter.' },
  { title: 'Optimize the acoustics', line: 'Positions and panels, calculated.' },
  { title: 'See the result', line: 'Hear it before you buy.' },
];

/** The journey's one label: it orients, the steps explain. */
export const HOME_JOURNEY_LABEL = 'How it works';

/** The journey's last step, made real: the listening room. */
export const HOME_LISTEN = {
  heading: 'Press play.',
  /** A fact the demo needs: a room's bass and decay vanish on laptop speakers. */
  caption: 'Headphones on.',
  before: 'Before',
  after: 'After',
  play: 'Play',
  pause: 'Pause',
  failed: 'Audio unavailable',
} as const;

/** The rooms it plays, first is the default: the theater, whose echo is the
 *  most audible change, and the hero's room. Keyed by the goal each showcase
 *  room is picked for (landing/showcase/showcaseTabs.ts). */
export const HOME_LISTEN_ROOMS = [
  { goal: 'theater', label: 'Theater' },
  { goal: 'listening-space', label: 'Hi-fi' },
  { goal: 'music-studio', label: 'Studio' },
] as const;

/** After the payoff: the feeling of owning the thing (owner, 2026-09-26: "a
 *  deep, enthusiastic and hobbyist love for the system you have"), carried by
 *  a wall of real gear rather than by a claim. */
export const HOME_LOVE = {
  heading: "A system you'll love.",
  products: 'products',
  brands: 'brands',
  browse: 'Browse the gear',
} as const;

export interface StartOption {
  intent: 'budget' | 'speakers' | 'room' | 'owned';
  title: string;
}

/** The launchpad: the four places a visitor can already be, in their words —
 *  first person, so each reads as "this is me" rather than as a category. */
export const HOME_START_OPTIONS: readonly StartOption[] = [
  { intent: 'budget', title: 'I have a budget' },
  { intent: 'speakers', title: 'I know my speakers' },
  { intent: 'room', title: "Here's my room" },
  { intent: 'owned', title: 'I already own gear' },
];

export const HOME_START = {
  heading: 'Start with what you have.',
  /** The general way in, beside the four specific ones. */
  scratch: 'Start from scratch',
} as const;

export interface HomeFaqItem {
  question: string;
  answer: string;
}

/** The homepage's FAQ: rendered by Faq.tsx AND emitted as FAQPage JSON-LD
 *  from this same array (faqPage()), so the two can never drift — the
 *  pattern every other FAQ block on the site already follows
 *  (components/seo/FaqSection.tsx). */
export const HOME_FAQ: readonly HomeFaqItem[] = [
  {
    question: 'Do I need an account to use it?',
    answer:
      'No, you can draw a room and run the full analysis as a guest, no sign-up. You only need an account when you want to save a room and come back to it later.',
  },
  {
    question: 'How do I measure my room?',
    answer: 'A tape measure is enough. You do not need centimetre precision for treatment to be accurate.',
  },
  {
    question: 'Does it handle odd-shaped rooms?',
    answer:
      'Yes. Sloped and vaulted ceilings, attic rooms with angled roofs, and L-shapes are all supported. You can also place doors and windows on any wall and set their size and position.',
  },
  {
    question: 'How does it decide what treatment to use?',
    answer:
      'It models the sound field for your exact room and system, then tracks where each frequency band lands on the walls. Panels go where they have the highest measured impact based on frequency response and sound decay times across the entire frequency spectrum.',
  },
  {
    question: 'Does it account for my speakers and where they sit?',
    answer:
      'Yes, and it goes further. It can also optimize speaker and listening positions first, searching well over a hundred thousand placement and aiming combinations for the flattest response and the tightest stereo imaging. You can also choose which combination of components to optimize: subwoofer(s), speakers, or listener position.',
  },
  {
    question: 'Can I use it for a production studio, not just a listening room?',
    answer: 'Yes. There is a dedicated studio mode alongside the listening-room workflow.',
  },
  {
    question: 'Does it account for what my walls and floor are made of?',
    answer: 'Yes, it does. Each material within the room has its own unique absorption coefficient derived from frontier research.',
  },
  {
    question: 'How long does an analysis take?',
    answer: 'For position searches, anywhere from 2-10 seconds, and for panel placement, up to a minute.',
  },
  {
    question: 'How accurate is it?',
    answer:
      'The model is built from first principles and acoustics literature, with targets calibrated against measured rooms. It is a prediction rather than a microphone, but it is built to stay as objective as the physics allows.',
  },
];
